import axios, { AxiosRequestConfig } from "axios";
import { createErrorMessage } from "./zendeskHttpRequest.ts";
import { tokenManager } from "../tokenManager";

// proxy Lambda 内部でリトライを行うため、フロントエンド側のリトライは
// ネットワークエラー（Lambda 自体に到達できない場合）のみに限定する
const MAX_RETRIES = 1;
const RETRY_DELAY = 3000; // 3秒

/**
 * プロキシサーバー経由で HTTP リクエストを送信する関数
 *
 * 指定された URL に対して、認証トークンを付与してリクエストを送信します。
 * 必要に応じてレスポンスをバイナリ形式（`arraybuffer`）で取得することも可能です。
 * 429/ネットワークエラー時はリトライを行います。
 *
 * @template T - レスポンスデータの型（ジェネリクス）
 * @param {string} token - 認証トークン（Bearer トークン）
 * @param {string} url - リクエスト先の URL
 * @param {boolean} [binary=false] - バイナリデータを取得する場合 `true`（デフォルトは `false`）
 * @returns {Promise<T>} - レスポンスデータ
 * @throws {Error} - リクエストに失敗した場合、エラーメッセージを含む例外をスロー
 */
export const proxyHttpRequest = async <T>(
  token: string,
  url: string,
  binary: boolean = false,
) => {
  // TokenManager から最新トークンを取得
  const freshToken = tokenManager.getToken() || token;

  const config: AxiosRequestConfig = {
    url: `${import.meta.env.VITE_PROXY_URL}/proxy?url=${encodeURIComponent(url)}`,
    headers: {
      Authorization: `Bearer ${freshToken}`,
    },
    responseType: binary ? "arraybuffer" : "json",
    timeout: 30000, // 30秒タイムアウト（proxy Lambda の実行タイムアウトに合わせる）
  };

  let retries = 0;
  while (retries <= MAX_RETRIES) {
    try {
      const result = await axios.request<T>(config);
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        // Lambda 内部で 429/500 のリトライ済みなので、フロントでは
        // ネットワークエラー（Lambda 自体に到達できない場合）のみリトライ
        const isRetryable =
          !error.response && error.request; // ネットワークエラー

        if (isRetryable && retries < MAX_RETRIES) {
          const retryAfter = error.response?.headers?.["retry-after"];
          const delay = retryAfter
            ? parseInt(retryAfter) * 1000
            : RETRY_DELAY * (retries + 1); // バックオフ
          console.log(
            `リトライ可能なエラー (${status || "network"}). ${delay}ms 待機後にリトライします... (${retries + 1}/${MAX_RETRIES})`,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          retries++;
          continue;
        }
      }
      // バイナリリクエストの場合、プロキシ経由で失敗したら元URLに直接フォールバック
      // （Zendesk の content_url は署名付きURLなので認証不要でアクセス可能）
      if (binary) {
        try {
          console.log(`プロキシ経由で失敗。元URLに直接フォールバック: ${url}`);
          const directResult = await axios.get<T>(url, {
            responseType: "arraybuffer",
            timeout: 30000,
          });
          return directResult.data;
        } catch (directError) {
          console.error("直接ダウンロードも失敗:", directError);
        }
      }

      const errorMessage = createErrorMessage(error);
      throw new Error(errorMessage);
    }
  }
  throw new Error("リトライ回数上限");
};
